import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Tooltip,
  IconButton,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  Skeleton,
  useMediaQuery,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import DownloadIcon from "@mui/icons-material/Download";
import InboxIcon from "@mui/icons-material/Inbox";
import { url } from "../Components/url";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useListViewMode } from "../hooks/useListPreferences";
import { GlobalSearchBar } from "../Components/ERPList";
import listStyles from "./AllEnquiries.module.scss";
import * as XLSX from "xlsx";
import { fetchWithAuth } from "../Components/FetchWithAuth";
import { useTheme } from "../hooks/useTheme";
import { createMuiThemeFromMode } from "../theme/theme";
import { toast } from "react-toastify";

const LIST_ID = "server-logs";
const PAGE_SIZE_OPTIONS = [10, 12, 25, 50, 100];

const baseThemeOptions = {
  palette: {
    secondary: { main: "#f5365c" },
    success: { main: "#2dce89" },
    warning: { main: "#fb6340" },
    info: { main: "#11cdef" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: { styleOverrides: { root: { boxShadow: "none" } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
};

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: "#ffffff",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledCard = styled(Card)(({ theme }) => ({
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[6],
  },
}));

function levelChipColor(level) {
  switch (level) {
    case "INFO":
      return "success";
    case "WARNING":
      return "warning";
    case "ERROR":
      return "error";
    case "DEBUG":
      return "default";
    default:
      return "info";
  }
}

function getSortValue(log, key) {
  switch (key) {
    case "timestamp":
      return new Date(log.timestamp).getTime() || 0;
    case "level":
      return String(log.level || "").toLowerCase();
    case "username":
      return String(log.username || "").toLowerCase();
    case "action":
      return String(log.action || "").toLowerCase();
    case "details":
      return String(log.details || "").toLowerCase();
    case "ip_address":
      return String(log.ip_address || "").toLowerCase();
    case "endpoint":
      return `${String(log.method || "")} ${String(log.endpoint || "")}`.toLowerCase();
    default:
      return "";
  }
}

function compareLogs(a, b, orderBy, order) {
  const aVal = getSortValue(a, orderBy);
  const bVal = getSortValue(b, orderBy);
  let cmp;
  if (orderBy === "timestamp") {
    cmp = (Number(aVal) || 0) - (Number(bVal) || 0);
  } else {
    cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: "base" });
  }
  if (cmp !== 0) return order === "asc" ? cmp : -cmp;
  return String(a.id).localeCompare(String(b.id), undefined, { numeric: true });
}

const LogsPage = () => {
  const { themeMode } = useTheme();
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode]
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filterOpen, setFilterOpen] = useState(!isMobile);
  const [globalSearch, setGlobalSearch] = useState("");
  const debouncedSearch = useDebouncedValue(globalSearch, 400);
  const [viewMode, setViewMode] = useListViewMode(LIST_ID, "table");
  const toggleViewMode = () => setViewMode((prev) => (prev === "cards" ? "table" : "cards"));
  const [filters, setFilters] = useState({
    level: "",
    user_id: "",
    action: "",
    date_from: "",
    date_to: "",
  });
  const debouncedFilters = useDebouncedValue(filters, 400);
  const [sortConfig, setSortConfig] = useState({ key: "timestamp", direction: "desc" });

  const filterKey = useMemo(() => JSON.stringify(debouncedFilters), [debouncedFilters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterKey]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        per_page: String(pageSize),
      });
      Object.entries(debouncedFilters).forEach(([k, v]) => {
        if (v !== "" && v != null) params.set(k, v);
      });

      const response = await fetchWithAuth(`${url}/logs?${params}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setTotalPages(Math.max(1, data.pages || 1));
        setTotalCount(typeof data.total === "number" ? data.total : (data.logs || []).length);
        if (typeof data.current_page === "number") setCurrentPage(data.current_page);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.error || "Failed to load logs.");
        setLogs([]);
        setTotalCount(0);
      }
    } catch (error) {
      toast.error("Error fetching logs.");
      setLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, debouncedFilters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      level: "",
      user_id: "",
      action: "",
      date_from: "",
      date_to: "",
    });
    setCurrentPage(1);
  };

  const searchLower = (debouncedSearch || "").trim().toLowerCase();
  const filteredLogs = useMemo(() => {
    if (!searchLower) return logs;
    return logs.filter((log) => {
      const user = (log.username || "").toLowerCase();
      const action = (log.action || "").toLowerCase();
      const details = (log.details || "").toLowerCase();
      const endpoint = (log.endpoint || "").toLowerCase();
      return (
        user.includes(searchLower) ||
        action.includes(searchLower) ||
        details.includes(searchLower) ||
        endpoint.includes(searchLower)
      );
    });
  }, [logs, searchLower]);

  const sortedLogs = useMemo(() => {
    const rows = [...filteredLogs];
    const { key, direction } = sortConfig;
    if (!key) return rows;
    rows.sort((a, b) => compareLogs(a, b, key, direction));
    return rows;
  }, [filteredLogs, sortConfig]);

  const requestSort = (property) => {
    setSortConfig((prev) => ({
      key: property,
      direction: prev.key === property && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleExportToExcel = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(debouncedFilters).forEach(([k, v]) => {
        if (v !== "" && v != null) params.set(k, v);
      });
      const response = await fetchWithAuth(`${url}/logs_excel?${params}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!response.ok) {
        toast.error("Failed to export logs.");
        return;
      }
      const data = await response.json();
      const rows = data.logs || [];
      if (rows.length === 0) {
        toast.info("No logs to export for the current filters.");
        return;
      }
      const worksheetData = rows.map((log) => ({
        Timestamp: new Date(log.timestamp).toLocaleString(),
        Level: log.level,
        User: log.username,
        Action: log.action,
        Details: log.details,
        IP_Address: log.ip_address,
        Endpoint: `${log.method} ${log.endpoint}`,
      }));
      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Logs");
      XLSX.writeFile(workbook, "system_logs.xlsx");
    } catch {
      toast.error("Export failed.");
    }
  };

  const safePage = Math.min(currentPage, totalPages);
  const rangeStart = totalCount === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, totalCount);

  const displayRows = sortedLogs;
  const isServerEmpty = !loading && totalCount === 0;
  const searchNoMatch =
    Boolean(searchLower) && logs.length > 0 && displayRows.length === 0;

  return (
    <ThemeProvider theme={theme}>
      <Box className={listStyles.root}>
        <Paper elevation={0} className={listStyles.contentPaper}>
          <Box sx={{ marginBottom: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box>
                <Typography variant="h4" className={listStyles.pageTitle}>
                  System Logs
                </Typography>
                <Typography variant="body2" className={listStyles.pageSubtitle} sx={{ mt: 0.5 }}>
                  View system activity and audit logs.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box className={listStyles.header}>
            <GlobalSearchBar
              value={globalSearch}
              onChange={setGlobalSearch}
              placeholder="Search user, action, details, endpoint…"
            />
            <Box className={listStyles.headerActions}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setFilterOpen(!filterOpen)}
                sx={{ textTransform: "none" }}
              >
                {filterOpen ? "Hide Filters" : "Show Filters"}
              </Button>
              <Tooltip title={viewMode === "cards" ? "Table View" : "Card View"}>
                <IconButton onClick={toggleViewMode} color="primary" size="small">
                  {viewMode === "cards" ? <ViewListIcon /> : <ViewModuleIcon />}
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExportToExcel} size="small">
                Download as Excel
              </Button>
            </Box>
          </Box>

          {filterOpen && (
            <Fade in={filterOpen}>
              <FilterPaper variant="outlined">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="logs-level-label">Level</InputLabel>
                      <Select
                        labelId="logs-level-label"
                        name="level"
                        value={filters.level}
                        label="Level"
                        onChange={handleFilterChange}
                      >
                        <MenuItem value="">All Levels</MenuItem>
                        <MenuItem value="INFO">INFO</MenuItem>
                        <MenuItem value="WARNING">WARNING</MenuItem>
                        <MenuItem value="ERROR">ERROR</MenuItem>
                        <MenuItem value="DEBUG">DEBUG</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="User ID"
                      name="user_id"
                      value={filters.user_id}
                      onChange={handleFilterChange}
                      placeholder="Filter by user ID"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Action"
                      name="action"
                      value={filters.action}
                      onChange={handleFilterChange}
                      placeholder="Filter by action"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="From Date"
                      name="date_from"
                      type="date"
                      value={filters.date_from}
                      onChange={handleFilterChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="To Date"
                      name="date_to"
                      type="date"
                      value={filters.date_to}
                      onChange={handleFilterChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} className={listStyles.filterActions}>
                    <Button variant="outlined" onClick={handleResetFilters}>
                      Reset Filters
                    </Button>
                  </Grid>
                </Grid>
              </FilterPaper>
            </Fade>
          )}

          {loading && (
            <Box className={listStyles.loadingContainer}>
              {viewMode === "cards" ? (
                <Grid container spacing={2}>
                  {[...Array(6)].map((_, i) => (
                    <Grid item xs={12} sm={6} md={4} key={i}>
                      <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box>
                  <Skeleton variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
                  <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
                </Box>
              )}
            </Box>
          )}

          {isServerEmpty && (
            <Box className={listStyles.emptyState}>
              <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No logs found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting filters or search, or pick another page.
              </Typography>
            </Box>
          )}

          {!loading && !isServerEmpty && (
            <>
              <Box className={listStyles.pagination}>
                <Typography variant="body2" color="text.secondary">
                  Showing {rangeStart}–{rangeEnd} of {totalCount} results
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel id="logs-page-size-label">Per page</InputLabel>
                    <Select
                      labelId="logs-page-size-label"
                      value={pageSize}
                      label="Per page"
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Pagination
                    count={totalPages}
                    page={safePage}
                    onChange={(_, p) => setCurrentPage(p)}
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              </Box>

              {viewMode === "cards" ? (
                searchNoMatch ? (
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No logs on this page match your search. Clear the search or change page.
                  </Typography>
                ) : (
                  <Grid container spacing={2} className={listStyles.cardsGrid}>
                    {displayRows.map((log) => (
                      <Grid item xs={12} sm={6} md={4} key={log.id}>
                        <StyledCard variant="outlined">
                          <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                              <Chip label={log.level} color={levelChipColor(log.level)} size="small" variant="outlined" />
                              <Typography variant="caption" color="text.secondary">
                                {new Date(log.timestamp).toLocaleString()}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>User:</strong> {log.username}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>Action:</strong> {log.action}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap title={log.details}>
                              <strong>Details:</strong> {log.details || "—"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                              {log.method} {log.endpoint}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              IP: {log.ip_address}
                            </Typography>
                          </CardContent>
                        </StyledCard>
                      </Grid>
                    ))}
                  </Grid>
                )
              ) : (
                <Paper variant="outlined" className={listStyles.tableContainer}>
                  <Table stickyHeader size="small" className={listStyles.enquiriesTable}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={listStyles.colLogTime} sortDirection={sortConfig.key === "timestamp" ? sortConfig.direction : false}>
                          <TableSortLabel
                            active={sortConfig.key === "timestamp"}
                            direction={sortConfig.key === "timestamp" ? sortConfig.direction : "asc"}
                            onClick={() => requestSort("timestamp")}
                          >
                            Timestamp
                          </TableSortLabel>
                        </TableCell>
                        <TableCell className={listStyles.colLogLevel} sortDirection={sortConfig.key === "level" ? sortConfig.direction : false}>
                          <TableSortLabel
                            active={sortConfig.key === "level"}
                            direction={sortConfig.key === "level" ? sortConfig.direction : "asc"}
                            onClick={() => requestSort("level")}
                          >
                            Level
                          </TableSortLabel>
                        </TableCell>
                        <TableCell className={listStyles.colLogUser} sortDirection={sortConfig.key === "username" ? sortConfig.direction : false}>
                          <TableSortLabel
                            active={sortConfig.key === "username"}
                            direction={sortConfig.key === "username" ? sortConfig.direction : "asc"}
                            onClick={() => requestSort("username")}
                          >
                            User
                          </TableSortLabel>
                        </TableCell>
                        <TableCell className={listStyles.colLogAction} sortDirection={sortConfig.key === "action" ? sortConfig.direction : false}>
                          <TableSortLabel
                            active={sortConfig.key === "action"}
                            direction={sortConfig.key === "action" ? sortConfig.direction : "asc"}
                            onClick={() => requestSort("action")}
                          >
                            Action
                          </TableSortLabel>
                        </TableCell>
                        <TableCell className={listStyles.colLogDetails} sortDirection={sortConfig.key === "details" ? sortConfig.direction : false}>
                          <TableSortLabel
                            active={sortConfig.key === "details"}
                            direction={sortConfig.key === "details" ? sortConfig.direction : "asc"}
                            onClick={() => requestSort("details")}
                          >
                            Details
                          </TableSortLabel>
                        </TableCell>
                        <TableCell className={listStyles.colLogIp} sortDirection={sortConfig.key === "ip_address" ? sortConfig.direction : false}>
                          <TableSortLabel
                            active={sortConfig.key === "ip_address"}
                            direction={sortConfig.key === "ip_address" ? sortConfig.direction : "asc"}
                            onClick={() => requestSort("ip_address")}
                          >
                            IP Address
                          </TableSortLabel>
                        </TableCell>
                        <TableCell className={listStyles.colLogEndpoint} sortDirection={sortConfig.key === "endpoint" ? sortConfig.direction : false}>
                          <TableSortLabel
                            active={sortConfig.key === "endpoint"}
                            direction={sortConfig.key === "endpoint" ? sortConfig.direction : "asc"}
                            onClick={() => requestSort("endpoint")}
                          >
                            Endpoint
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {searchNoMatch ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography color="text.secondary" sx={{ py: 2 }}>
                              No logs on this page match your search. Clear the search or change page.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        displayRows.map((log) => (
                          <TableRow key={log.id} hover>
                            <TableCell className={listStyles.colLogTime}>
                              <Typography variant="body2" className={listStyles.primaryText} noWrap>
                                {new Date(log.timestamp).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell className={listStyles.colLogLevel}>
                              <Chip label={log.level} color={levelChipColor(log.level)} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell className={listStyles.colLogUser}>
                              <Typography variant="body2" className={listStyles.primaryText} noWrap>
                                {log.username}
                              </Typography>
                            </TableCell>
                            <TableCell className={listStyles.colLogAction}>
                              <Typography variant="body2" noWrap title={log.action}>
                                {log.action}
                              </Typography>
                            </TableCell>
                            <TableCell className={listStyles.colLogDetails}>
                              <Typography variant="body2" className={listStyles.secondaryText} noWrap title={log.details}>
                                {log.details}
                              </Typography>
                            </TableCell>
                            <TableCell className={listStyles.colLogIp}>
                              <Typography variant="body2" noWrap>
                                {log.ip_address}
                              </Typography>
                            </TableCell>
                            <TableCell className={listStyles.colLogEndpoint}>
                              <Typography variant="body2" className={listStyles.secondaryText} noWrap title={`${log.method} ${log.endpoint}`}>
                                {log.method} {log.endpoint}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </>
          )}
        </Paper>
      </Box>
    </ThemeProvider>
  );
};

export default LogsPage;
